import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle2, UserPlus, ShieldAlert, Mail, Building } from 'lucide-react';
import { toast } from 'sonner';

export default function RegisterPage() {
    const [searchParams] = useSearchParams();
    const invitationToken = searchParams.get('invitation');
    const companyInvitationToken = searchParams.get('company_invitation');
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [invitationInfo, setInvitationInfo] = useState(null);
    const [companyInvitationInfo, setCompanyInvitationInfo] = useState(null);
    const [loadingInvitation, setLoadingInvitation] = useState(false);
    const [invitationError, setInvitationError] = useState(false);
    const { register, loginWithGoogle } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (invitationToken) {
            verifyInvitation();
        }
        if (companyInvitationToken) {
            verifyCompanyInvitation();
        }
    }, [invitationToken, companyInvitationToken]);

    const verifyInvitation = async () => {
        setLoadingInvitation(true);
        setInvitationError(false);
        try {
            const response = await authAPI.verifyInvitation(invitationToken);
            setInvitationInfo(response.data);
            setEmail(response.data.email);
        } catch (error) {
            setInvitationError(true);
            toast.error('Link undangan tidak valid atau sudah kadaluarsa');
        } finally {
            setLoadingInvitation(false);
        }
    };

    const verifyCompanyInvitation = async () => {
        setLoadingInvitation(true);
        try {
            const API_BASE = process.env.REACT_APP_BACKEND_URL;
            const res = await fetch(`${API_BASE}/api/auth/verify-company-invitation?token=${companyInvitationToken}`);
            if (res.ok) {
                const data = await res.json();
                setCompanyInvitationInfo(data);
                setEmail(data.email);
            } else {
                setInvitationError(true);
                toast.error('Link undangan company tidak valid atau sudah kadaluarsa');
            }
        } catch (err) {
            setInvitationError(true);
        } finally {
            setLoadingInvitation(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        setLoading(true);
        try {
            // Pass both invitation tokens
            const effectiveInvitationToken = invitationToken || null;
            await register(name, email, password, effectiveInvitationToken, companyInvitationToken);
            if (companyInvitationInfo) {
                toast.success(`Selamat! Anda adalah Owner dari ${companyInvitationInfo.company_name}`);
            } else if (invitationInfo) {
                toast.success(`Welcome! You've joined ${invitationInfo.workspace_name}`);
            } else {
                toast.success('Account created successfully!');
            }
            navigate('/');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create account');
        } finally {
            setLoading(false);
        }
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const handleGoogleSuccess = async (credentialResponse) => {
        setGoogleLoading(true);
        try {
            await loginWithGoogle(credentialResponse.credential);
            toast.success('Welcome!');
            navigate('/');
        } catch (error) {
            console.error('Google login error:', error);
            toast.error(error.response?.data?.detail || 'Failed to sign up with Google');
        } finally {
            setGoogleLoading(false);
        }
    };

    const handleGoogleError = () => {
        toast.error('Google sign up failed. Please try again.');
    };

    if (loadingInvitation) {
        return (
            <div className="auth-page">
                <div className="flex items-center justify-center w-full h-screen">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </div>
        );
    }

    // If no invitation token and no company invitation, show "invitation required" message
    if ((!invitationToken && !companyInvitationToken) || invitationError) {
        return (
            <div className="auth-page">
                <div className="auth-left">
                    <div className="auth-left-content">
                        <div className="mb-8">
                            <h1 className="font-heading text-4xl font-bold mb-2">TaskGlory</h1>
                            <p className="text-[#A7E3C8] text-lg">Organize. Track. Achieve.</p>
                        </div>
                        <div className="space-y-6">
                            <div className="flex items-start gap-4">
                                <CheckCircle2 className="w-6 h-6 text-[#A7E3C8] mt-0.5" strokeWidth={1.5} />
                                <div>
                                    <h3 className="font-semibold mb-1">Flexible Hierarchy</h3>
                                    <p className="text-[#EAF4F1]/80 text-sm">Workspaces, Spaces, Folders, Lists & unlimited subtasks</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <CheckCircle2 className="w-6 h-6 text-[#A7E3C8] mt-0.5" strokeWidth={1.5} />
                                <div>
                                    <h3 className="font-semibold mb-1">Custom Fields</h3>
                                    <p className="text-[#EAF4F1]/80 text-sm">Add any field type to track what matters to you</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <CheckCircle2 className="w-6 h-6 text-[#A7E3C8] mt-0.5" strokeWidth={1.5} />
                                <div>
                                    <h3 className="font-semibold mb-1">Time Tracking</h3>
                                    <p className="text-[#EAF4F1]/80 text-sm">Built-in timer, manual logs, and billable hours</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="auth-right">
                    <div className="w-full max-w-sm text-center">
                        {/* Invitation Required Message */}
                        <div className="mb-8 p-6 bg-brand-warning/10 border border-brand-warning/30 rounded-xl">
                            <ShieldAlert className="w-16 h-16 text-brand-warning mx-auto mb-4" strokeWidth={1.5} />
                            <h2 className="font-heading text-2xl font-bold text-brand-text-primary mb-3">
                                Pendaftaran Khusus Undangan
                            </h2>
                            <p className="text-brand-text-secondary mb-4">
                                Untuk mendaftar di TaskGlory, Anda memerlukan undangan dari admin workspace.
                            </p>
                            <div className="p-4 bg-white/50 rounded-lg border border-brand-border">
                                <Mail className="w-6 h-6 text-brand-primary mx-auto mb-2" strokeWidth={1.5} />
                                <p className="text-sm text-brand-text-muted">
                                    Hubungi admin workspace Anda untuk mendapatkan link undangan via email.
                                </p>
                            </div>
                        </div>
                        
                        <p className="text-sm text-brand-text-secondary">
                            Sudah punya akun?{' '}
                            <Link to="/login" className="text-brand-primary font-medium hover:underline" data-testid="login-link">
                                Masuk di sini
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page">
            <div className="auth-left">
                <div className="auth-left-content">
                    <div className="mb-8">
                        <h1 className="font-heading text-4xl font-bold mb-2">TaskGlory</h1>
                        <p className="text-[#A7E3C8] text-lg">Organize. Track. Achieve.</p>
                    </div>
                    <div className="space-y-6">
                        <div className="flex items-start gap-4">
                            <CheckCircle2 className="w-6 h-6 text-[#A7E3C8] mt-0.5" strokeWidth={1.5} />
                            <div>
                                <h3 className="font-semibold mb-1">Flexible Hierarchy</h3>
                                <p className="text-[#EAF4F1]/80 text-sm">Workspaces, Spaces, Folders, Lists & unlimited subtasks</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <CheckCircle2 className="w-6 h-6 text-[#A7E3C8] mt-0.5" strokeWidth={1.5} />
                            <div>
                                <h3 className="font-semibold mb-1">Custom Fields</h3>
                                <p className="text-[#EAF4F1]/80 text-sm">Add any field type to track what matters to you</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <CheckCircle2 className="w-6 h-6 text-[#A7E3C8] mt-0.5" strokeWidth={1.5} />
                            <div>
                                <h3 className="font-semibold mb-1">Time Tracking</h3>
                                <p className="text-[#EAF4F1]/80 text-sm">Built-in timer, manual logs, and billable hours</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="auth-right">
                <div className="w-full max-w-sm">
                    {/* Invitation Banner */}
                    {invitationInfo && (
                        <div className="mb-6 p-4 bg-brand-primary/10 border border-brand-primary/20 rounded-lg">
                            <div className="flex items-center gap-3">
                                <UserPlus className="w-8 h-8 text-brand-primary" strokeWidth={1.5} />
                                <div>
                                    <p className="text-sm font-medium text-brand-text-primary">
                                        Anda diundang ke workspace
                                    </p>
                                    <p className="text-lg font-bold text-brand-primary">
                                        {invitationInfo.workspace_name}
                                    </p>
                                    <p className="text-xs text-brand-text-muted">
                                        oleh {invitationInfo.invited_by} • Role: {invitationInfo.role}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Company Invitation Banner */}
                    {companyInvitationInfo && (
                        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                                    <Building className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-brand-text-primary">
                                        Undangan Owner Company
                                    </p>
                                    <p className="text-lg font-bold text-amber-700">
                                        {companyInvitationInfo.company_name}
                                    </p>
                                    <p className="text-xs text-brand-text-muted">
                                        Daftar untuk menjadi owner company ini
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mb-8">
                        <h2 className="font-heading text-2xl font-bold text-brand-text-primary mb-2">
                            Buat Akun untuk Bergabung
                        </h2>
                        <p className="text-brand-text-secondary">
                            Lengkapi data untuk bergabung ke workspace
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-brand-text-primary font-medium">Full Name</Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="John Doe"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                className="bg-brand-background border-brand-border focus:ring-[#2F6F63]/30"
                                data-testid="register-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-brand-text-primary font-medium">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={!!invitationInfo}
                                className="bg-brand-background border-brand-border focus:ring-[#2F6F63]/30"
                                data-testid="register-email-input"
                            />
                            {invitationInfo && (
                                <p className="text-xs text-brand-text-muted">Email sesuai dengan undangan</p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-brand-text-primary font-medium">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                className="bg-brand-background border-brand-border focus:ring-[#2F6F63]/30"
                                data-testid="register-password-input"
                            />
                            <p className="text-xs text-brand-text-muted">Minimum 6 characters</p>
                        </div>
                        <Button
                            type="submit"
                            className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white h-11"
                            disabled={loading}
                            data-testid="register-submit-btn"
                        >
                            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Daftar & Bergabung
                        </Button>
                    </form>
                    <p className="mt-6 text-center text-sm text-brand-text-secondary">
                        Sudah punya akun?{' '}
                        <Link to="/login" className="text-brand-primary font-medium hover:underline" data-testid="login-link">
                            Masuk di sini
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
